import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import PageHelmet from "../Helper/PageHelmet";
import { Link } from "react-router-dom";
import {
  LuFileText,
  LuAlertCircle,
  LuSplitSquareHorizontal,
} from "react-icons/lu";
import MidstreamContactForm from "./MidstreamContactForm";
import MidstreamFooter from "./MidstreamFooter";
import upstreamHero from "../../assets/midstream-product-images/upstream.jpg";
import upstreamProduction from "../../assets/midstream-product-images/upstream_production.png";
import upstreamRecovery from "../../assets/midstream-product-images/upstream_recovery.png";
import upstreamAar from "../../assets/midstream-product-images/upstream_aar.png";
import soc2Seal from "../../assets/midstream-product-images/soc2-seal.png";
import logoMark from "../../assets/logos/header_logo.png";

const navLinks = [
  { label: "The Problem", href: "#problem" },
  { label: "What we do", href: "#what-we-do" },
  { label: "Where the value is", href: "#money" },
  { label: "How we started", href: "#how" },
];

const footerLinks = [
  { label: "The problem", href: "#problem" },
  { label: "What we do", href: "#what-we-do" },
  { label: "Where the value is", href: "#money" },
  { label: "How we start", href: "#how" },
  { label: "Find out in two weeks", href: "#contact" },
];

const heroStats = [
  { value: "Workovers", label: "Wells that need a workover, found in the data and priced before you commit" },
  { value: "Shut-ins", label: "Every shut-in and restart analyzed for what it cost" },
  { value: "Live data", label: "Live production data linked to the reservoir and well models your engineers already use" },
];

const problems = [
  {
    icon: LuFileText,
    title: "One engineer, one model.",
    text: "Or a tool someone built two budget cycles ago. When they move on, the answer moves with them.",
  },
  {
    icon: LuAlertCircle,
    title: "The field and the P&L in different rooms.",
    text: "Surveillance sees the drop today. Finance sees the dollar impact next month.",
  },
  {
    icon: LuSplitSquareHorizontal,
    title: "No audit trail for capital.",
    text: "The board, the lender, or a buyer asks why this well got the rig. The workbook can't say.",
  },
];

const pillars = [
  {
    tag: "Engineering rigor",
    title: "Physics behind every number.",
    text: "Your engineers can inspect every assumption. Your CFO knows the physics has already been checked.",
  },
  {
    tag: "Investor-defensible",
    title: "An audit trail for every decision.",
    text: "Scenario, approval, outcome, and the math behind each — one click away.",
  },
  {
    tag: "Agents",
    title: "Agents that work overnight.",
    text: "They run against your data while you sleep, so the daily production meeting happens on exception.",
  },
];

const flow = [
  { label: "Wellhead", tone: "mint" },
  { label: "Gathering", tone: "mint" },
  { label: "Plant", tone: "mint" },
  { label: "Pipeline", tone: "amber" },
  { label: "Settlement", tone: "amber" },
];

const moneyPlaces = [
  {
    tag: "Capital Decision",
    title: "The capital decision",
    image: upstreamHero,
    text: "The capital decision. Workovers, recompletions, and new wells, priced against today’s strip, today’s constraints, and the physics of your reservoir. Know why this well is next, and what waiting costs.",
  },
  {
    tag: "Daily Production decision",
    title: "The daily production decision.",
    image: upstreamProduction,
    text: "Lift, choke, intervention, routing, and facility constraints, priced against current prices and available takeaway. The question moves from \"how much can we make\" to \"what is each barrel worth to us today.\"",
  },
  {
    tag: "Production Recovery",
    title: "When production drops.",
    image: upstreamRecovery,
    text: "A unit trips. A well goes down. Takeaway tightens. In minutes, Dsider shows what to defer, what to restore first, and what each option costs. It's still a bad day. It stops being a bad quarter.",
  },
  {
    tag: "After-action review",
    title: "The after-action review.",
    image: upstreamAar,
    text: "Dsider separates what price did, what operations did, and what the subsurface did, so the month-end conversation is about what to learn.",
  },
];

const alsoInModel = [
  "Offshore and onshore surveillance triage.",
  "Facility and produced-water constraints.",
  "Decline forecast reconciliation.",
];

const steps = [
  {
    when: "Weeks 1–2",
    title: "Discovery.",
    text: "Send us the production data, well files, and facility exports you already have. Together we pick the one decision worth solving first. A short, paid engagement.",
  },
  {
    when: "Days 15–90",
    title: "Prove the number.",
    text: "We build the model on your data. Your lead reservoir or production engineer reviews it. At day 90 you have a directional value number your CFO can check.",
  },
  {
    when: "Then",
    title: "Decide with your eyes open.",
    text: "If the number isn't there, you've spent a few hours over the quarter reviewing our work. If it is, you sign knowing exactly what you're buying.",
  },
];

const audiences = [
  {
    tag: "In the engineering review",
    title: "A first-principles simulation that sits on top of the tools you already run.",
    text: "That includes HYSYS. Dsider reads your SCADA history as it is. Every assumption is visible and can be changed. Put your director of engineering in the first review and verify the physics.",
    points: [
      "Every assumption is inspectable",
      "Built on your SCADA data as-is",
      "HYSYS-compatible",
      "Your director of engineering reviews first",
    ],
  },
  {
    tag: "In the boardroom",
    title: "An AI story with a dollar sign — grounded in your own physics.",
    seal: true,
    text: "Agents grounded in the physics of your own system that found real EBITDA and can show exactly how. Dsider is SOC 2 Type 1 compliant, so it clears your compliance review.",
    points: [
      "Real EBITDA, with the math behind it",
      "Every decision defensible to a buyer or lender",
      "SOC 2 Type 1 compliant.",
      "No black boxes: physics-grounded AI",
    ],
  },
];

const simRows = [
  ["Train I", "224.0", "Train II", "198.6"],
  ["Train III", "186.2", "Train IV", "210.4"],
  ["Compression", "370.0", "Pipeline", "265.8"],
];

const HOVER_DELAY = 250;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el)
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
};

// Stagger index for entrance animations (read as var(--d) in the SCSS)
const d = (i) => ({ "--d": i });

const Upstream = () => {
  const [activeId, setActiveId] = useState("");
  const hoverTimer = useRef(null);
  const rootRef = useRef(null);
  const moneyRef = useRef(null);
  const pinRef = useRef(null);
  const trackRef = useRef(null);
  const dotsRef = useRef(null);
  const hintRef = useRef(null);

  // Reveal .ms-reveal elements once as they scroll into view
  useEffect(() => {
    const items = rootRef.current.querySelectorAll(".ms-reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Highlight the nav link of the section currently in view.
  // Nothing is highlighted while the hero (above the first section) is in view.
  useEffect(() => {
    const sections = navLinks
      .map((l) => document.getElementById(l.href.slice(1)))
      .filter(Boolean);
    let frame = null;

    const updateActive = () => {
      frame = null;
      const line = window.innerHeight * 0.4;
      let current = "";
      sections.forEach((el) => {
        if (el.getBoundingClientRect().top <= line) current = el.id;
      });
      setActiveId(current);
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      clearTimeout(hoverTimer.current);
    };
  }, []);

  // "Where the money is": on tablet/desktop the section pins below the nav and
  // vertical scroll moves the card track sideways (1:1, lightly smoothed).
  // It unpins once the last card is in place. Tab / arrow keys move card by card.
  useEffect(() => {
    const section = moneyRef.current;
    const pin = pinRef.current;
    const sticky = pin.firstElementChild;
    const track = trackRef.current;
    const dots = Array.from(dotsRef.current.children);
    const hint = hintRef.current;
    const cards = Array.from(track.children);
    const wide = window.matchMedia("(min-width: 768px)");
    let enabled = false;
    let distance = 0;
    let pinTop = 0;
    let current = 0;
    let target = 0;
    let frame = null;
    let active = -1;

    const render = () => {
      const progress = distance ? current / distance : 0;
      track.style.transform = `translate3d(${-current}px, 0, 0)`;
      // highlight the card (and dot) matching the scroll position
      const next = Math.round(progress * (cards.length - 1));
      if (next !== active) {
        active = next;
        cards.forEach((c, i) => c.classList.toggle("is-active", i === active));
        dots.forEach((dot, i) => dot.classList.toggle("is-active", i === active));
      }
      hint.classList.toggle("is-done", progress > 0.98);
    };

    // ease the track toward the scroll position
    const tick = () => {
      frame = null;
      current += (target - current) * 0.2;
      if (Math.abs(target - current) < 0.5) current = target;
      render();
      if (current !== target) frame = requestAnimationFrame(tick);
    };

    const readTarget = () => {
      const scrolled = pinTop - pin.getBoundingClientRect().top;
      target = Math.min(distance, Math.max(0, scrolled));
    };

    const onScroll = () => {
      if (!enabled) return;
      readTarget();
      if (frame === null) frame = requestAnimationFrame(tick);
    };

    const measure = () => {
      enabled = wide.matches && !prefersReducedMotion();
      section.classList.toggle("is-hscroll", enabled);
      track.style.transform = "";
      pin.style.height = "";
      active = -1;
      distance = 0;
      current = 0;
      target = 0;
      if (!enabled) return;
      pinTop = parseFloat(getComputedStyle(sticky).top) || 0;
      distance = Math.max(0, track.scrollWidth - track.clientWidth);
      // extra height = horizontal travel, so scroll maps 1:1
      pin.style.height = `${sticky.offsetHeight + distance}px`;
      readTarget();
      current = target;
      render();
    };

    // scroll the page so the track sits at horizontal offset x
    const scrollToOffset = (x) => {
      const offset = Math.min(distance, Math.max(0, x));
      window.scrollTo({
        top: window.scrollY + pin.getBoundingClientRect().top - pinTop + offset,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    };

    const cardOffset = (card) => card.offsetLeft - cards[0].offsetLeft;

    // dots jump to the scroll position where that card is active
    const dotHandlers = dots.map((dot, i) => {
      const handler = () => scrollToOffset((i / (cards.length - 1)) * distance);
      dot.addEventListener("click", handler);
      return handler;
    });

    const onFocusIn = (e) => {
      const card = e.target.closest(".ms-money-card");
      if (!enabled || !card) return;
      sticky.scrollLeft = 0; // undo any focus scrolling inside the clipped area
      scrollToOffset(cardOffset(card));
    };

    const onKeyDown = (e) => {
      if (!enabled || (e.key !== "ArrowRight" && e.key !== "ArrowLeft")) return;
      if (e.target.closest && e.target.closest("input, textarea, select")) return;
      const rect = pin.getBoundingClientRect();
      const pinned =
        rect.top <= pinTop + 1 && rect.bottom >= pinTop + sticky.offsetHeight - 1;
      const focused = cards.indexOf(document.activeElement);
      if (!pinned && focused < 0) return;
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      if (focused >= 0) {
        const next = cards[focused + dir];
        if (next) next.focus({ preventScroll: true });
        return;
      }
      const step = cards[1] ? cardOffset(cards[1]) : 0;
      scrollToOffset(target + dir * step);
    };

    measure();
    const resizeObserver =
      "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(sticky);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    window.addEventListener("keydown", onKeyDown);
    track.addEventListener("focusin", onFocusIn);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("keydown", onKeyDown);
      track.removeEventListener("focusin", onFocusIn);
      dots.forEach((dot, i) => dot.removeEventListener("click", dotHandlers[i]));
      if (frame !== null) cancelAnimationFrame(frame);
      section.classList.remove("is-hscroll");
    };
  }, []);

  // Smooth-scroll every in-page link (nav, buttons, footer)
  const handleAnchorClick = (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    e.preventDefault();
    clearTimeout(hoverTimer.current);
    scrollToSection(link.getAttribute("href").slice(1));
  };

  const handleNavEnter = (id) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => scrollToSection(id), HOVER_DELAY);
  };

  const handleNavLeave = () => clearTimeout(hoverTimer.current);

  return (
    <>
      <PageHelmet pageTitle="Upstream" />

      <div className="midstream upstream" ref={rootRef} onClick={handleAnchorClick}>
        {/* Nav */}
        <nav className="ms-nav">
          <Container className="ms-nav-inner">
            <Link to="/" className="ms-brand">
              <img src={logoMark} alt="Dsider" />
              <span>Dsider</span>
            </Link>
            <span className="ms-tag">Upstream</span>
            <ul className="ms-nav-links">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className={activeId === l.href.slice(1) ? "is-active" : ""}
                    onMouseEnter={() => handleNavEnter(l.href.slice(1))}
                    onMouseLeave={handleNavLeave}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <a href="#contact" className="ms-btn ms-btn-sm">
              Find out in two weeks
            </a>
          </Container>
        </nav>

        {/* Hero */}
        <section className="ms-hero">
          <Container>
            <Row className="ms-hero-row align-items-center g-5">
              <Col lg={6} className="ms-hero-copy">
                <span className="ms-badge ms-hero-in" style={d(0)}>
                  For Upstream operators
                </span>
                <h1 className="ms-title">
                  <span className="ms-hero-in" style={d(1)}>There's value still</span>
                  <span className="ms-hero-in" style={d(2)}>in your field.</span>
                  <em className="ms-hero-in" style={d(3)}>
                    <span>We can tell you how</span>
                    <span>much.</span>
                  </em>
                </h1>
                <p className="ms-lead ms-hero-in" style={d(4)}>
                  Dsider links reservoir, wellbore, facilities, and contracts in one
                  model and evaluates every call in dollars, so your production,
                  deferral, and capital decisions are ones you can defend.
                </p>
                <div className="ms-actions ms-hero-in" style={d(5)}>
                  <a href="#contact" className="ms-btn">
                    Find out in two weeks &rarr;
                  </a>
                  <a href="#what-we-do" className="ms-btn ms-btn-dark">
                    See what Dsider does &rarr;
                  </a>
                </div>
                <div className="ms-hero-stats">
                  {heroStats.map((s, i) => (
                    <div key={s.value} className="ms-hero-in" style={d(6 + i)}>
                      <strong>{s.value}</strong>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </Col>
              <Col lg={6} className="ms-hero-media">
                <div className="ms-dash-wrap ms-hero-in ms-from-right" style={d(2)}>
                  <img
                    src={upstreamHero}
                    alt="Dsider upstream dashboard"
                    className="ms-dash"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* The problem */}
        <section className="ms-section ms-problem" id="problem">
          <Container>
            <span className="ms-eyebrow ms-reveal">The problem</span>
            <h2 className="ms-heading ms-heading-wide ms-reveal" style={d(1)}>
              <span className="ms-nowrap">The most expensive upstream</span>
              <br />
              <span className="ms-nowrap">
                decisions are made with <em>the least</em>
              </span>
              <br />
              <em>visibility.</em>
            </h2>
            <p className="ms-lead ms-problem-lead ms-reveal" style={d(2)}>
              Which wells to choke back. Which intervention to defer. Whether to
              produce into a weak price or shut in. Which pad gets capital next
              quarter. Most teams make those calls with a decline curve, a
              spreadsheet, and the judgment of a few experienced people. It works
              until the day it doesn't, and then nobody can show the math.
            </p>
            <Row className="g-4">
              {problems.map(({ icon: Icon, title, text }, i) => (
                <Col md={4} key={title}>
                  <div className="ms-problem-card ms-reveal" style={d(i)}>
                    <span className="ms-icon">
                      <Icon />
                    </span>
                    <h4>{title}</h4>
                    <p>{text}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* What we do */}
        <section className="ms-section ms-what" id="what-we-do">
          <Container>
            <Row className="g-5 align-items-center">
              <Col lg={6}>
                <h2 className="ms-heading ms-reveal" style={d(1)}>
                  From reservoir to Net Present Value,{" "}
                  <em>in one model.</em>
                </h2>
                <p className="ms-lead ms-what-lead ms-reveal" style={d(2)}>
                  Dsider connects to the reservoir and well models you already use,
                  follows production through the facility to the sales point, lays
                  your commercial terms and prices on top, and evaluates every
                  decision in dollars, with the rationale recorded.
                </p>
                <div className="ms-flow ms-reveal" style={d(3)}>
                  {flow.map((f, i) => (
                    <div
                      className={`ms-flow-step ms-${f.tone}`}
                      key={f.label}
                      style={d(i)}
                    >
                      <span className="ms-flow-dot" />
                      <span className="ms-flow-label">{f.label}</span>
                    </div>
                  ))}
                </div>
                <div className="ms-flow-note ms-reveal" style={d(4)}>
                  <span className="ms-badge">
                    Dsider prices every decision along the way
                  </span>
                </div>
              </Col>
              <Col lg={6}>
                <div className="ms-stack">
                  {pillars.map((p, i) => (
                    <div className="ms-pillar ms-reveal" key={p.title} style={d(i + 1)}>
                      <span className="ms-pillar-tag">{p.tag}</span>
                      <h4>{p.title}</h4>
                      <p>{p.text}</p>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Where the money is */}
        <section className="ms-section ms-money" id="money" ref={moneyRef}>
          {/* Pinned horizontal scroll on tablet/desktop; see the effect above */}
          <div className="ms-hscroll-pin" ref={pinRef}>
            <div className="ms-hscroll-sticky">
              <Container>
                <div className="ms-hscroll-head">
                  <div>
                    <span className="ms-eyebrow">Where the Value is hiding</span>
                    <h2 className="ms-heading ms-reveal" style={d(1)}>
                      <span className="ms-nowrap">Where the value is hiding</span>
                    </h2>
                    <p className="ms-lead ms-money-lead ms-reveal" style={d(2)}>
                      One mid-cap operator's own business case put better daily
                      operating and planning decisions at $0.8 to $1.7 million
                      of EBITDA per plant per year.
                    </p>
                  </div>
                </div>
                <div className="ms-reveal" style={d(3)}>
                  <div
                    className="ms-hscroll-track"
                    ref={trackRef}
                    role="region"
                    aria-label="Where the value is hiding"
                  >
                    {moneyPlaces.map((m, i) => (
                      <div
                        className="ms-money-card"
                        key={m.title}
                        tabIndex={0}
                        role="group"
                        aria-label={`${i + 1} of ${moneyPlaces.length}: ${m.title}`}
                      >
                        <div className="ms-money-head">
                          <span className="ms-money-num">0{i + 1}</span>
                          <span className={`ms-money-tag${m.tone ? ` is-${m.tone}` : ""}`}>
                            {m.tag}
                          </span>
                        </div>
                        <h4>{m.title}</h4>
                        <div className="ms-money-shot">
                          <img src={m.image} alt={m.title} />
                        </div>
                        <p>{m.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ms-hscroll-foot">
                  <div className="ms-hscroll-dots" ref={dotsRef}>
                    {moneyPlaces.map((m, i) => (
                      <button
                        type="button"
                        key={m.title}
                        aria-label={`Show card ${i + 1}: ${m.title}`}
                      />
                    ))}
                  </div>
                  <span className="ms-hscroll-hint" ref={hintRef}>
                    Scroll to explore <span aria-hidden="true">&rarr;</span>
                  </span>
                </div>
              </Container>
            </div>
          </div>
          <Container>
            <div className="ms-also ms-reveal">
              <span className="ms-also-label">Also in the model</span>
              {alsoInModel.map((item) => (
                <span className="ms-also-chip" key={item}>
                  {item}
                </span>
              ))}
            </div>
          </Container>
        </section>

        {/* How we start */}
        <section className="ms-section ms-start" id="how">
          <Container>
            <span className="ms-eyebrow ms-reveal">How we start</span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>
              Two weeks to a defined problem.{" "}
              <em>Ninety days to a number.</em>
            </h2>
            <p className="ms-start-lead ms-reveal" style={d(2)}>
              You know what you're buying before you buy it.
            </p>
            <div className="ms-steps">
              {steps.map((s, i) => (
                <div className="ms-step ms-reveal" key={s.title} style={d(i)}>
                  <span className="ms-step-num">0{i + 1}</span>
                  <span className="ms-step-when">{s.when}</span>
                  <h4>{s.title}</h4>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
            <div className="ms-start-cta ms-reveal">
              <p>No live integration required to start. Our team builds. Yours reviews.</p>
              <a href="#contact" className="ms-btn">
                Find out in two weeks &rarr;
              </a>
            </div>
          </Container>
        </section>

        {/* Who it's built for */}
        <section className="ms-section ms-built">
          <Container>
            <span className="ms-eyebrow ms-reveal">Built to hold up.</span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>
              Numbers that hold up{" "}
              <em>in every room.</em>
            </h2>
            <Row className="g-4">
              {audiences.map((a, i) => (
                <Col md={6} key={a.tag}>
                  <div className="ms-built-card ms-reveal" style={d(i)}>
                    <span className="ms-money-tag">{a.tag}</span>
                    <h3>{a.title}</h3>
                    {a.seal ? (
                      <img
                        src={soc2Seal}
                        alt="AICPA SOC 2"
                        className="ms-seal"
                      />
                    ) : (
                      <div className="ms-sim">
                        <span className="ms-sim-title">HYSYS · Plant model</span>
                        {simRows.map((row) => (
                          <div className="ms-sim-row" key={row[0]}>
                            {[0, 2].map((i) => (
                              <div className="ms-sim-cell" key={row[i]}>
                                <span>{row[i]}</span>
                                <strong>{row[i + 1]}</strong>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    <p>{a.text}</p>
                    <ul className="ms-list">
                      {a.points.map((pt) => (
                        <li key={pt}>{pt}</li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Get started */}
        <section className="ms-section ms-cta" id="contact">
          <Container>
            <span className="ms-eyebrow ms-reveal">Get started</span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>Are you leaving money on the table?</h2>
            <p className="ms-lead ms-reveal" style={d(2)}>
              Most operators suspect they are. Few can measure it. Two weeks
              tells you.
            </p>
            <div className="ms-reveal" style={d(3)}>
              <MidstreamContactForm />
            </div>
          </Container>
        </section>

        <MidstreamFooter
          segment="Upstream"
          tagline="Decision intelligence for upstream operators. Reservoir to value, in one model."
          links={footerLinks}
        />
      </div>
    </>
  );
};

export default Upstream;
